import React, { useEffect, useState } from "react";
import api, { formatPHP, formatDate, formatErrorDetail, API_BASE } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import { Download, FileSpreadsheet, Plus, Trash2, Camera } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function ComplianceReports() {
    const [snapshots, setSnapshots] = useState([]);
    const [active, setActive] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [runs, setRuns] = useState([]);
    const [busy, setBusy] = useState("");

    const load = async () => {
        const { data } = await api.get("/compliance/snapshots");
        setSnapshots(data || []);
    };
    useEffect(() => { load(); }, []);

    const openCreate = async () => {
        setCreateOpen(true);
        try {
            const { data } = await api.get("/compliance/eligible-runs");
            setRuns(data || []);
        } catch { setRuns([]); }
    };

    const createSnap = async (run) => {
        setBusy(run.id);
        try {
            const { data } = await api.post(`/compliance/runs/${run.id}/snapshot`);
            toast.success(`Snapshot generated for ${run.period_start} → ${run.period_end}`);
            setCreateOpen(false);
            await load();
            setActive(data);
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to generate snapshot"); }
        setBusy("");
    };

    const deleteSnap = async (s, ev) => {
        ev.stopPropagation();
        if (!window.confirm(`Delete the compliance snapshot for ${s.period_start} → ${s.period_end}? The government report exports for this period will be unavailable until it is regenerated.`)) return;
        try {
            await api.delete(`/compliance/snapshots/${s.id}`);
            toast.success("Snapshot deleted");
            if (active?.id === s.id) setActive(null);
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Delete failed"); }
    };

    const dl = async (snapId, kind, label) => {
        try {
            const r = await api.get(`/compliance/snapshots/${snapId}/${kind}`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement("a"); a.href = url; a.download = `${label}.xlsx`; a.click(); URL.revokeObjectURL(url);
        } catch { toast.error("Export failed"); }
    };

    return (
        <div>
            <PageHeader title="Government Compliance Reports" subtitle="SSS, PhilHealth, Pag-IBIG, and BIR reports generated from immutable payroll snapshots."
                actions={
                    <button onClick={openCreate} className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" data-testid="new-snapshot-btn">
                        <Plus className="size-4" /> New Snapshot
                    </button>
                } />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <Surface className="p-4 lg:col-span-1">
                    <h3 className="font-display font-semibold text-sm mb-2">Available Snapshots</h3>
                    <div className="space-y-2 max-h-[60vh] overflow-auto">
                        {snapshots.map((s) => (
                            <div key={s.id} onClick={() => setActive(s)} className={`w-full text-left p-3 rounded-md border cursor-pointer flex items-start justify-between gap-2 ${active?.id === s.id ? "border-primary bg-primary/5" : "border-border bg-background"}`} data-testid={`snap-${s.id}`}>
                                <div>
                                    <div className="font-medium text-sm">{s.period_start} → {s.period_end}</div>
                                    <div className="text-xs text-muted-foreground">{s.employee_count} employees · generated {formatDate(s.generated_at)}</div>
                                </div>
                                <button className="text-muted-foreground hover:text-destructive shrink-0 mt-0.5" title="Delete snapshot" onClick={(e) => deleteSnap(s, e)} data-testid={`del-snap-${s.id}`}>
                                    <Trash2 className="size-4" />
                                </button>
                            </div>
                        ))}
                        {!snapshots.length && <Empty title="No snapshots" subtitle="Click “New Snapshot” to generate one from a locked payroll run." />}
                    </div>
                </Surface>

                <Surface className="p-4 lg:col-span-2">
                    {active ? (
                        <>
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <h3 className="font-display font-semibold">Snapshot · {active.period_start} → {active.period_end}</h3>
                                    <div className="text-xs text-muted-foreground">{active.employee_count} employees</div>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
                                <div className="border border-border rounded-md p-2"><div className="text-[10px] uppercase text-muted-foreground">SSS Total (EE+ER)</div><div className="font-mono font-semibold">{formatPHP((active.totals?.sss_ee || 0) + (active.totals?.sss_er || 0))}</div></div>
                                <div className="border border-border rounded-md p-2"><div className="text-[10px] uppercase text-muted-foreground">PhilHealth Total</div><div className="font-mono font-semibold">{formatPHP((active.totals?.philhealth_ee || 0) + (active.totals?.philhealth_er || 0))}</div></div>
                                <div className="border border-border rounded-md p-2"><div className="text-[10px] uppercase text-muted-foreground">Pag-IBIG Total</div><div className="font-mono font-semibold">{formatPHP((active.totals?.pagibig_ee || 0) + (active.totals?.pagibig_er || 0))}</div></div>
                                <div className="border border-border rounded-md p-2"><div className="text-[10px] uppercase text-muted-foreground">Withholding Tax</div><div className="font-mono font-semibold">{formatPHP(active.totals?.wht)}</div></div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 mb-4">
                                <button onClick={() => dl(active.id, "sss", `SSS_R3_${active.period_start}`)} className="h-10 px-3 rounded-md border border-border bg-background text-sm flex items-center justify-center gap-2" data-testid="dl-sss"><FileSpreadsheet className="size-4" /> SSS R3</button>
                                <button onClick={() => dl(active.id, "philhealth", `PhilHealth_RF1_${active.period_start}`)} className="h-10 px-3 rounded-md border border-border bg-background text-sm flex items-center justify-center gap-2" data-testid="dl-ph"><FileSpreadsheet className="size-4" /> PhilHealth RF1</button>
                                <button onClick={() => dl(active.id, "pagibig", `PagIBIG_MCRF_${active.period_start}`)} className="h-10 px-3 rounded-md border border-border bg-background text-sm flex items-center justify-center gap-2" data-testid="dl-pi"><FileSpreadsheet className="size-4" /> Pag-IBIG MCRF</button>
                                <button onClick={() => dl(active.id, "bir-alphalist", `BIR_Alphalist_${active.period_start}`)} className="h-10 px-3 rounded-md border border-border bg-background text-sm flex items-center justify-center gap-2" data-testid="dl-bir"><FileSpreadsheet className="size-4" /> BIR Alphalist</button>
                            </div>
                            <div className="mb-4">
                                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">⚡ E-file formats — upload straight to the government portals</div>
                                <div className="grid grid-cols-3 gap-2">
                                    <a className="h-9 px-2 rounded-md border border-primary/40 text-primary text-xs flex items-center justify-center gap-1" href={`/api/compliance/snapshots/${active.id}/sss-r3-file`} data-testid="efile-sss"><Download className="size-3" /> SSS R-3 .txt</a>
                                    <a className="h-9 px-2 rounded-md border border-primary/40 text-primary text-xs flex items-center justify-center gap-1" href={`/api/compliance/snapshots/${active.id}/philhealth-efile`} data-testid="efile-ph"><Download className="size-3" /> PhilHealth EPRS .csv</a>
                                    <a className="h-9 px-2 rounded-md border border-primary/40 text-primary text-xs flex items-center justify-center gap-1" href={`/api/compliance/snapshots/${active.id}/pagibig-efile`} data-testid="efile-pi"><Download className="size-3" /> Pag-IBIG MCRF .csv</a>
                                </div>
                            </div>
                            <div className="overflow-auto">
                                <table className="w-full dense-table">
                                    <thead>
                                        <tr>
                                            <th>Emp No</th><th>Name</th><th>SSS</th><th>PhilHealth</th><th>Pag-IBIG</th><th>TIN</th>
                                            <th className="num">Gross</th><th className="num">Net</th><th className="num">WHT</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(active.rows || []).map((r, i) => (
                                            <tr key={i}>
                                                <td className="font-mono">{r.employee_no}</td>
                                                <td>{r.name}</td>
                                                <td className="font-mono text-xs">{r.sss_no || <span className="text-destructive">missing</span>}</td>
                                                <td className="font-mono text-xs">{r.philhealth_no || <span className="text-destructive">missing</span>}</td>
                                                <td className="font-mono text-xs">{r.pagibig_no || <span className="text-destructive">missing</span>}</td>
                                                <td className="font-mono text-xs">{r.tin || <span className="text-destructive">missing</span>}</td>
                                                <td className="num">{formatPHP(r.gross)}</td>
                                                <td className="num">{formatPHP(r.net_pay)}</td>
                                                <td className="num">{formatPHP(r.withholding_tax)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    ) : <Empty title="Select a snapshot" subtitle="Choose a snapshot from the left to view and export." />}
                </Surface>
            </div>

            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Generate compliance snapshot</DialogTitle></DialogHeader>
                    <div className="text-xs text-muted-foreground mb-1">Pick a locked payroll run. Generating again for the same period replaces the old snapshot.</div>
                    <div className="space-y-1.5 max-h-[50vh] overflow-auto">
                        {runs.map((r) => (
                            <div key={r.id} className="flex items-center justify-between border border-border rounded-md p-2.5" data-testid={`run-${r.id}`}>
                                <div>
                                    <div className="text-sm font-medium font-mono">{r.period_start} → {r.period_end}</div>
                                    <div className="text-[11px] text-muted-foreground capitalize">{r.status}{r.has_snapshot ? " · snapshot exists" : ""}</div>
                                </div>
                                <button disabled={busy === r.id} onClick={() => createSnap(r)}
                                    className={`h-8 px-3 rounded-md text-xs font-medium flex items-center gap-1 ${r.has_snapshot ? "border border-border" : "bg-primary text-white"}`}
                                    data-testid={`gen-${r.id}`}>
                                    <Camera className="size-3" /> {busy === r.id ? "Generating…" : r.has_snapshot ? "Regenerate" : "Generate"}
                                </button>
                            </div>
                        ))}
                        {!runs.length && <Empty title="No locked payroll runs" subtitle="Lock a payroll run first, then generate its snapshot." />}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
